/**
 * HistorieWorkspace — the shared admin view of change history (design:
 * .agents/historie-zmen-plan.md). Mounted as the "Historie" body of a module's
 * Správa page; `moduly` lists the history modules that page covers (the
 * servis workspace shows Servis + Uskladnění), already filtered to the ones
 * the viewer administers.
 *
 * Toolband: submodule chip (module · entity) · author chip · record ID ·
 * date range · reset. Body: one entry per save on one record — when, who,
 * which record, then its changes (field old → new, collection add/remove, or
 * the snapshot of a created record). Newest first, cursor-paginated.
 */

import { useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Menu } from '@/components/Menu';
import { SearchInput } from '@/components/SearchInput';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import {
  historieKdy,
  historieZmenaText,
  useHistorieInfiniteQuery,
  useHistorieMetaQueries,
  type HistorieModul,
  type HistorieZaznam,
  type HistorieZmena,
} from './api';

interface HistorieWorkspaceProps {
  /** History modules this page covers (non-empty; admin-filtered by the caller). */
  moduly: [HistorieModul, ...HistorieModul[]];
}

interface Submodul {
  modul: HistorieModul;
  entita: string;
  label: string;
}

const DATE_INPUT =
  'border border-line bg-paper px-2 py-1 font-mono text-[11px] text-ink-2 outline-none focus:border-ink';
const DATE_LABEL =
  'flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3';

export function HistorieWorkspace({ moduly }: HistorieWorkspaceProps) {
  const metaQueries = useHistorieMetaQueries(moduly);
  const metas = moduly.map((m, i) => ({ modul: m, query: metaQueries[i] }));

  const submoduly: Submodul[] = metas.flatMap(({ modul, query }) =>
    (query.data?.entity ?? []).map((e) => ({
      modul,
      entita: e.klic,
      label: moduly.length > 1 ? `${query.data!.label} · ${e.label}` : e.label,
    })),
  );

  const [vybrany, setVybrany] = useState<{ modul: HistorieModul; entita: string } | null>(null);
  const active: Submodul | undefined =
    submoduly.find((s) => s.modul === vybrany?.modul && s.entita === vybrany?.entita) ??
    submoduly[0];
  const modul = active?.modul ?? moduly[0];

  const [autor, setAutor] = useState<number | null>(null);
  const [idText, setIdText] = useState('');
  const [od, setOd] = useState('');
  const [doD, setDoD] = useState('');
  const dId = useDebouncedValue(idText.trim(), 300);

  const idZaznamu = /^\d+$/.test(dId) ? Number(dId) : undefined;
  const filters = useMemo(
    () => ({
      entita: active?.entita,
      id_zaznamu: idZaznamu,
      id_uzivatele: autor ?? undefined,
      od: od || undefined,
      do: doD || undefined,
    }),
    [active?.entita, idZaznamu, autor, od, doD],
  );

  const historie = useHistorieInfiniteQuery(modul, filters, { enabled: !!active });
  const zaznamy = historie.data?.pages.flatMap((p) => p.zaznamy) ?? [];
  const autori = metas.find((m) => m.modul === modul)?.query.data?.autori ?? [];
  const autorJmeno = autori.find((a) => a.id_uzivatele === autor)?.jmeno;
  const hasFilter = autor !== null || idText !== '' || od !== '' || doD !== '';

  const resetFilters = () => {
    setAutor(null);
    setIdText('');
    setOd('');
    setDoD('');
  };

  const metaError = metas.some((m) => m.query.isError);
  const metaLoading = metas.some((m) => m.query.isLoading);

  return (
    <>
      <Toolband>
        {/* The submodule is a required choice (there is no "all"), not a
            narrowing filter — so the chip never shows the active state. */}
        <Menu>
          <Menu.Trigger asChild>
            <Chip icon={<Icon name="filter" size={12} />}>
              {active?.label ?? 'Podmodul'}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Podmodul</Menu.Label>
            {submoduly.map((s) => (
              <Menu.CheckItem
                key={`${s.modul}:${s.entita}`}
                checked={s === active}
                onCheckedChange={(checked) => {
                  if (!checked) return;
                  setVybrany({ modul: s.modul, entita: s.entita });
                  // Authors and record ids are per module — start clean.
                  setAutor(null);
                  setIdText('');
                }}
              >
                {s.label}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <Menu>
          <Menu.Trigger asChild>
            <Chip icon={<Icon name="filter" size={12} />} active={autor !== null}>
              {autorJmeno ?? 'Kdokoli'}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Kdo změnu provedl</Menu.Label>
            <Menu.Item onSelect={() => setAutor(null)}>Kdokoli</Menu.Item>
            <Menu.Separator />
            {autori.map((a) => (
              <Menu.CheckItem
                key={a.id_uzivatele}
                checked={autor === a.id_uzivatele}
                onCheckedChange={(checked) => setAutor(checked ? a.id_uzivatele : null)}
              >
                {a.jmeno}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <SearchInput
          placeholder="ID záznamu…"
          inputMode="numeric"
          value={idText}
          onChange={(e) => setIdText(e.target.value)}
          wrapperClassName="max-w-[180px] flex-1"
          aria-label="ID záznamu"
        />
        <label className={DATE_LABEL}>
          Od
          <input type="date" value={od} onChange={(e) => setOd(e.target.value)} className={DATE_INPUT} />
        </label>
        <label className={DATE_LABEL}>
          Do
          <input type="date" value={doD} onChange={(e) => setDoD(e.target.value)} className={DATE_INPUT} />
        </label>
        <Toolband.Spacer />
        {hasFilter && (
          <button
            type="button"
            onClick={resetFilters}
            className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3 hover:text-ink hover:underline"
          >
            Vymazat filtr
          </button>
        )}
      </Toolband>

      <div
        data-testid="historie-workspace"
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-8 pb-8 pt-4 max-sm:px-4"
      >
        {metaError || historie.isError ? (
          <EmptyState eyebrow="Chyba" title="Historii nelze načíst" description="Zkuste obnovit stránku." />
        ) : metaLoading || historie.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : zaznamy.length === 0 ? (
          <EmptyState
            eyebrow="Bez záznamů"
            title={hasFilter ? 'Filtru neodpovídá žádná změna' : 'Zatím žádné změny'}
            description={hasFilter ? 'Upravte nebo vymažte filtr.' : undefined}
          />
        ) : (
          <>
            <ol className="flex flex-col border border-line bg-paper">
              {zaznamy.map((z) => (
                <HistorieEntry key={z.klic} zaznam={z} onRecord={(id) => setIdText(String(id))} />
              ))}
            </ol>
            {historie.hasNextPage && (
              <div className="flex justify-center">
                <Button
                  size="sm"
                  onClick={() => historie.fetchNextPage()}
                  disabled={historie.isFetchingNextPage}
                >
                  {historie.isFetchingNextPage ? 'Načítání…' : 'Načíst starší'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function HistorieEntry({
  zaznam,
  onRecord,
}: {
  zaznam: HistorieZaznam;
  onRecord: (id: number) => void;
}) {
  return (
    <li
      data-testid="historie-entry"
      className="flex flex-col gap-1.5 border-b border-line px-4 py-3 last:border-0"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[12.5px]">
        <time className="font-mono text-[11px] tabular-nums text-ink-3">
          {historieKdy(zaznam.vytvoreno_at)}
        </time>
        <b className="font-semibold text-ink">{zaznam.autor?.jmeno ?? 'Systém'}</b>
        <button
          type="button"
          onClick={() => onRecord(zaznam.id_zaznamu)}
          className="text-accent-text hover:underline"
          title="Zobrazit historii tohoto záznamu"
        >
          {zaznam.zaznam_label}
        </button>
      </div>
      <ul className="flex flex-col gap-1 pl-0.5">
        {zaznam.zmeny.map((zm) => (
          <HistorieZmenaRow key={zm.id_zmeny} zmena={zm} />
        ))}
      </ul>
    </li>
  );
}

function HistorieZmenaRow({ zmena }: { zmena: HistorieZmena }) {
  const sekce = zmena.sekce ? (
    <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
      {zmena.sekce_label}
    </span>
  ) : null;

  return (
    <li className="flex flex-col gap-0.5 text-[12.5px] text-ink-2">
      <div className="flex flex-wrap items-baseline gap-x-2">
        {sekce}
        {zmena.snapshot ? (
          <details className="group">
            <summary className="cursor-pointer text-ink marker:text-ink-3">
              {zmena.akce_label}
              {zmena.polozka ? ` · ${zmena.polozka}` : ''}
            </summary>
            <dl className="mt-1 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5 pl-4 text-[12px]">
              {zmena.snapshot.map((s) => (
                <div key={s.pole} className="contents">
                  <dt className="text-ink-3">{s.label}</dt>
                  <dd className="break-words text-ink-2">{s.hodnota}</dd>
                </div>
              ))}
            </dl>
          </details>
        ) : (
          <span className="break-words">{historieZmenaText(zmena)}</span>
        )}
      </div>
      {zmena.poznamka && <p className="pl-0.5 text-[12px] italic text-ink-3">{zmena.poznamka}</p>}
    </li>
  );
}
